import React, { useState } from 'react';
import type { DailyActivityItem, StatisticsPeriod } from '../../types/dashboard.types';

interface ActivityChartProps {
  dailyActivity: DailyActivityItem[];
  period: StatisticsPeriod;
  onPeriodChange: (p: StatisticsPeriod) => void;
  isLoading?: boolean;
}

export const ActivityChart: React.FC<ActivityChartProps> = ({
  dailyActivity,
  period,
  onPeriodChange,
  isLoading = false,
}) => {
  const [hoveredDay, setHoveredDay] = useState<DailyActivityItem | null>(null);

  const maxVal = Math.max(4, ...dailyActivity.map((d) => d.reviews));
  const totalReviews = dailyActivity.reduce((acc, d) => acc + d.reviews, 0);

  const formatDateLabel = (dateStr: string) => {
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="rounded-2xl p-6 border border-white/10 bg-surface-800/80 backdrop-blur-sm space-y-5">
      {/* Header & Period Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-white tracking-tight">Learning & Review Activity</h3>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-500/20 text-primary-300 border border-primary-500/30">
              {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            Daily review frequency and recall progress over time
          </p>
        </div>

        {/* Period Selector Tabs */}
        <div className="inline-flex rounded-xl bg-surface-900/80 p-1 border border-white/10 self-start sm:self-auto">
          <button
            type="button"
            id="period-7d-btn"
            onClick={() => onPeriodChange('7d')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              period === '7d'
                ? 'bg-primary-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            7 Days
          </button>
          <button
            type="button"
            id="period-30d-btn"
            onClick={() => onPeriodChange('30d')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              period === '30d'
                ? 'bg-primary-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            30 Days
          </button>
          <button
            type="button"
            id="period-all-btn"
            onClick={() => onPeriodChange('all')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              period === 'all'
                ? 'bg-primary-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            All Time
          </button>
        </div>
      </div>

      {/* Chart Canvas Area */}
      <div className="relative pt-4">
        {isLoading ? (
          <div className="h-44 flex items-center justify-center">
            <span className="text-xs text-slate-400 animate-pulse">Loading activity chart...</span>
          </div>
        ) : dailyActivity.length === 0 ? (
          <div className="h-44 flex items-center justify-center text-center">
            <p className="text-xs text-slate-500">No activity data available for this range.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Hover Tooltip Box */}
            <div className="h-6 flex items-center">
              {hoveredDay ? (
                <div className="text-xs text-slate-300 flex items-center gap-3 animate-fade-in font-medium">
                  <span className="text-white font-bold">{formatDateLabel(hoveredDay.date)}:</span>
                  <span className="text-emerald-400 font-bold">{hoveredDay.reviews} reviews</span>
                  <span className="text-slate-400 hidden sm:inline">
                    ({hoveredDay.breakdown.good} Good, {hoveredDay.breakdown.easy} Easy,{' '}
                    {hoveredDay.breakdown.hard} Hard, {hoveredDay.breakdown.again} Again)
                  </span>
                </div>
              ) : (
                <span className="text-[11px] text-slate-500 italic">
                  Hover over bars to inspect daily recall details
                </span>
              )}
            </div>

            {/* Bars Container */}
            <div className="h-36 flex items-end gap-1 sm:gap-2 px-1 border-b border-white/10 pb-2">
              {dailyActivity.map((day) => {
                const heightPercent = Math.max(4, Math.round((day.reviews / maxVal) * 100));
                const hasReviews = day.reviews > 0;

                return (
                  <div
                    key={day.date}
                    onMouseEnter={() => setHoveredDay(day)}
                    onMouseLeave={() => setHoveredDay(null)}
                    className="flex-1 h-full flex flex-col justify-end items-center group cursor-pointer relative"
                  >
                    {/* Bar */}
                    <div
                      style={{ height: `${heightPercent}%` }}
                      className={`w-full max-w-[28px] rounded-t-lg transition-all duration-300 ${
                        hasReviews
                          ? 'bg-gradient-to-t from-primary-600 to-emerald-400 shadow-md shadow-primary-900/30 group-hover:from-primary-500 group-hover:to-emerald-300'
                          : 'bg-white/5 group-hover:bg-white/10'
                      }`}
                    />
                  </div>
                );
              })}
            </div>

            {/* Date Labels under chart */}
            <div className="flex justify-between text-[10px] text-slate-500 font-mono px-1 pt-1">
              <span>{formatDateLabel(dailyActivity[0]?.date ?? '')}</span>
              {dailyActivity.length > 2 && (
                <span className="hidden sm:inline">
                  {formatDateLabel(
                    dailyActivity[Math.floor(dailyActivity.length / 2)]?.date ?? '',
                  )}
                </span>
              )}
              <span>{formatDateLabel(dailyActivity[dailyActivity.length - 1]?.date ?? '')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
